<template>
  <Dialog
    v-model:visible="dialogVisible"
    :modal="true"
    :draggable="false"
    :closable="true"
    :breakpoints="{ '960px': '75vw', '640px': '90vw' }"
    style="width: 70vw"
    @update:visible="updateDialogVisibility"
  >
    <template #header>
      <div>
        <h3>{{ t('storeParameters.services.productRecommendations.modalSuppliersTitle') }}</h3>
      </div>
    </template>
    <div class="container">
      <!-- Search bar -->
      <div class="search-container mb-3">
        <span class="p-input-icon-left w-full">
          <i class="fa-thin fa-magnifying-glass" />
          <InputText
            v-model="supplier"
            :placeholder="t('storeParameters.services.productRecommendations.searchSupplierPlaceholder')"
            class="w-full"
            @input="handleSearch"
          />
        </span>
      </div>

      <!-- Labels -->
      <div class="flex labels-row mb-2">
        <div class="pane-label available-label">
          <span class="font-semibold">{{ t('storeParameters.services.productRecommendations.availableSuppliers') }}</span>
          <span class="count-badge">{{ suppliersAvailableList.length }}</span>
        </div>
        <div class="spacer" />
        <div class="pane-label chosen-label">
          <span class="font-semibold">{{ t('storeParameters.services.productRecommendations.selectedSuppliers') }}</span>
          <span class="count-badge selected">{{ suppliersChosenList.length }}</span>
        </div>
      </div>

      <!-- Main content -->
      <div class="flex pane-container">
        <!-- Available suppliers -->
        <div class="pane available-pane">
          <div
            v-if="isLoading && suppliersAvailableList.length === 0"
            class="loading-state"
          >
            <i class="fa-solid fa-spinner fa-spin fa-2x" />
            <p>{{ t('storeParameters.services.productRecommendations.loading') }}</p>
          </div>
          <div
            v-else-if="suppliersAvailableList.length === 0 && supplier.length > 0"
            class="empty-state"
          >
            <i class="fa-thin fa-industry fa-3x mb-2" />
            <p>{{ t('storeParameters.services.productRecommendations.noSupplierResults') }}</p>
          </div>
          <div
            v-else-if="suppliersAvailableList.length === 0"
            class="empty-state"
          >
            <i class="fa-thin fa-magnifying-glass fa-3x mb-2" />
            <p>{{ t('storeParameters.services.productRecommendations.searchSupplierHint') }}</p>
          </div>
          <div
            v-else
            ref="availableListRef"
            class="supplier-list"
            @scroll="handleAvailableScroll"
          >
            <div
              v-for="supplierItem in suppliersAvailableList"
              :key="supplierItem.id_shop_manufacturer"
              class="supplier-item"
              @click="selectItem(supplierItem)"
            >
              <div class="supplier-info">
                <span class="supplier-id">#{{ supplierItem.id_manufacturer }}</span>
                <span class="supplier-name">{{ supplierItem.name }}</span>
              </div>
              <i class="fa-solid fa-plus item-action add" />
            </div>
            <div
              v-if="isLoadingMore"
              class="loading-more"
            >
              <i class="fa-solid fa-spinner fa-spin" />
              <span>{{ t('storeParameters.services.productRecommendations.loadingMore') }}</span>
            </div>
            <div
              v-if="!hasMoreResults && suppliersAvailableList.length > 0"
              class="end-of-list"
            >
              <span>{{ t('storeParameters.services.productRecommendations.endOfList') }}</span>
            </div>
          </div>
        </div>

        <!-- Action hint -->
        <div class="action-buttons">
          <div class="action-hint">
            <i class="fa-solid fa-arrow-right" />
          </div>
        </div>

        <!-- Chosen suppliers -->
        <div class="pane chosen-pane">
          <div
            v-if="suppliersChosenList.length === 0"
            class="empty-state"
          >
            <i class="fa-thin fa-clipboard-list fa-3x mb-2" />
            <p>{{ t('storeParameters.services.productRecommendations.noSupplierSelection') }}</p>
          </div>
          <div
            v-else
            class="supplier-list"
          >
            <div
              v-for="supplierItem in suppliersChosenList"
              :key="supplierItem.id_shop_manufacturer"
              class="supplier-item chosen"
              @click="removeItem(supplierItem)"
            >
              <div class="supplier-info">
                <span class="supplier-id">#{{ supplierItem.id_manufacturer }}</span>
                <span class="supplier-name">{{ supplierItem.name }}</span>
              </div>
              <i class="fa-solid fa-minus item-action remove" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="flex justify-content-between align-items-center">
        <span class="selection-summary">
          {{ t('storeParameters.services.productRecommendations.suppliersSelected', { count: suppliersChosenList.length }) }}
        </span>
        <div class="flex gap-2">
          <Button
            class="p-button-secondary"
            @click="handleCancel"
          >
            {{ t('cancel') }}
          </Button>
          <Button
            class="validate-btn"
            @click="handleValidate"
          >
            <i class="fa-solid fa-check mr-2" />
            {{ t('storeParameters.services.productRecommendations.validate') }}
          </Button>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts">
import {
  defineComponent, PropType, ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import { ShopsManufacturers } from '@/types/generated-types/graphql';
import InputText from 'primevue/inputtext';
import ShopsManufacturersList from '@/composables/shops-manufacturers/ShopsManufacturers';

const PAGE_SIZE = 50;

export default defineComponent({
  name: 'SupplierSelection',

  components: {
    Dialog,
    Button,
    InputText,
  },

  props: {
    availableManufacturersArr: {
      type: Array as PropType<ShopsManufacturers[]>,
      required: true,
    },

    chosenManufacturersArr: {
      type: Array as PropType<ShopsManufacturers[]>,
      required: true,
    },

    manufacturerSearchStr: {
      type: String,
      required: true,
    },
  },

  emits: ['close-action-modal'],

  setup(props, context) {
    const { t } = useI18n();
    const dialogVisible = ref(true);
    const supplier = ref(props.manufacturerSearchStr);
    const suppliersAvailableList = ref<ShopsManufacturers[]>([...props.availableManufacturersArr]);
    const suppliersChosenList = ref<ShopsManufacturers[]>([...props.chosenManufacturersArr]);
    // Stocker les valeurs initiales pour annulation
    const initialChosenList = [...props.chosenManufacturersArr];
    const availableListRef = ref<HTMLElement | null>(null);

    const isLoading = ref(false);
    const isLoadingMore = ref(false);
    const hasMoreResults = ref(true);
    const currentOffset = ref(0);
    let searchTimer: ReturnType<typeof setTimeout> | null = null;
    let searchRequestId = 0;

    const handleCancel = () => {
      dialogVisible.value = false;
      // Retourner les valeurs initiales
      context.emit('close-action-modal', initialChosenList, props.availableManufacturersArr, props.manufacturerSearchStr);
    };

    const handleValidate = () => {
      dialogVisible.value = false;
      context.emit('close-action-modal', suppliersChosenList.value, suppliersAvailableList.value, supplier.value);
    };

    const updateDialogVisibility = (visible: boolean) => {
      if (!visible) {
        // Fermeture via croix = annulation
        handleCancel();
      }
    };

    const filterOutChosen = (suppliers: ShopsManufacturers[]): ShopsManufacturers[] => suppliers.filter(
      (s) => !suppliersChosenList.value.some((chosen) => chosen.id_shop_manufacturer === s.id_shop_manufacturer),
    );

    const handleSearch = async () => {
      if (searchTimer) clearTimeout(searchTimer);

      searchRequestId += 1;
      const currentRequestId = searchRequestId;

      const searchTerm = supplier.value.trim();
      if (!searchTerm) {
        suppliersAvailableList.value = [];
        isLoading.value = false;
        return;
      }

      searchTimer = setTimeout(async () => {
        if (currentRequestId !== searchRequestId) return;

        isLoading.value = true;
        currentOffset.value = 0;
        hasMoreResults.value = true;
        suppliersAvailableList.value = [];

        const result = await ShopsManufacturersList(supplier.value, PAGE_SIZE, 0);

        if (result === null || currentRequestId !== searchRequestId) return;

        suppliersAvailableList.value = filterOutChosen(result);
        hasMoreResults.value = result.length === PAGE_SIZE;
        currentOffset.value = PAGE_SIZE;
        isLoading.value = false;
      }, 500);
    };

    const loadMore = async () => {
      if (isLoadingMore.value || !hasMoreResults.value) return;

      searchRequestId += 1;
      const currentRequestId = searchRequestId;

      isLoadingMore.value = true;
      const result = await ShopsManufacturersList(supplier.value, PAGE_SIZE, currentOffset.value);

      if (result === null || currentRequestId !== searchRequestId) {
        isLoadingMore.value = false;
        return;
      }

      if (result.length > 0) {
        const newSuppliers = filterOutChosen(result);
        suppliersAvailableList.value = [...suppliersAvailableList.value, ...newSuppliers];
        hasMoreResults.value = result.length === PAGE_SIZE;
        currentOffset.value += PAGE_SIZE;
      } else {
        hasMoreResults.value = false;
      }
      isLoadingMore.value = false;
    };

    const handleAvailableScroll = (event: Event) => {
      const target = event.target as HTMLElement;
      const { scrollTop, scrollHeight, clientHeight } = target;

      if (scrollHeight - scrollTop - clientHeight < 100) {
        loadMore();
      }
    };

    const selectItem = (supplierItem: ShopsManufacturers) => {
      suppliersChosenList.value.unshift(supplierItem);
      suppliersAvailableList.value = suppliersAvailableList.value.filter(
        (s) => s.id_shop_manufacturer !== supplierItem.id_shop_manufacturer,
      );
    };

    const removeItem = (supplierItem: ShopsManufacturers) => {
      suppliersChosenList.value = suppliersChosenList.value.filter(
        (s) => s.id_shop_manufacturer !== supplierItem.id_shop_manufacturer,
      );
    };

    return {
      t,
      dialogVisible,
      updateDialogVisibility,
      handleCancel,
      handleValidate,
      supplier,
      handleSearch,
      suppliersAvailableList,
      suppliersChosenList,
      availableListRef,
      selectItem,
      removeItem,
      handleAvailableScroll,
      isLoading,
      isLoadingMore,
      hasMoreResults,
    };
  },
});
</script>

<style scoped lang="scss">
@import '@/assets/scss/utilities/_variables.scss';

.container {
  padding: 1rem;
}

.search-container {
  .p-inputtext {
    padding-left: 2.5rem;
  }
}

.labels-row {
  .pane-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &.available-label {
      width: 45%;
    }

    &.chosen-label {
      width: 45%;
    }
  }

  .spacer {
    width: 10%;
  }

  .count-badge {
    background: $extra-light-grey;
    color: $tuna;
    padding: 0.15rem 0.5rem;
    border-radius: 1rem;
    font-size: 0.8rem;
    font-weight: 600;

    &.selected {
      background: lighten($brand-color-primary, 35%);
      color: darken($brand-color-primary, 20%);
    }
  }
}

.pane-container {
  height: 350px;
  gap: 0;
}

.pane {
  height: 100%;
  border: 1px solid $solitude;
  border-radius: 6px;
  overflow: hidden;
  background: $white;

  &.available-pane {
    width: 45%;
  }

  &.chosen-pane {
    width: 45%;
  }
}

.action-buttons {
  width: 10%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .action-hint {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $extra-light-grey;
    border-radius: 50%;
    color: $mid-grey;
  }
}

.supplier-list {
  height: 100%;
  overflow-y: auto;
  padding: 0.5rem;
}

.supplier-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: 0.25rem;

  &:hover {
    background: $extra-light-grey;

    .item-action {
      opacity: 1;
    }
  }

  &.chosen {
    background: lighten($brand-color-primary, 42%);

    &:hover {
      background: lighten($brand-color-primary, 38%);
    }
  }

  .item-action {
    opacity: 0;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    flex-shrink: 0;
    margin-left: auto;
    transition: all 0.2s ease;

    &.add {
      background: $brand-color-primary;
      color: $white;
    }

    &.remove {
      background: #dc3545;
      color: $white;
    }
  }
}

.supplier-info {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;

  .supplier-id {
    font-size: 0.75rem;
    color: $mid-grey;
    font-weight: 500;
  }

  .supplier-name {
    font-size: 0.875rem;
    color: $tuna;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.empty-state,
.loading-state {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: $mid-grey;
  text-align: center;
  padding: 1rem;

  i {
    color: $mid-grey;
    margin-bottom: 0.5rem;
  }

  p {
    margin: 0;
    font-size: 0.9rem;
  }
}

.loading-more,
.end-of-list {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  color: $mid-grey;
  font-size: 0.85rem;
}

.selection-summary {
  color: $mid-grey;
  font-size: 0.9rem;
}

.validate-btn {
  background: $brand-color-primary;
  border-color: $brand-color-primary;
  color: $white;

  &:hover {
    background: $successButtonHoverBg;
    border-color: $successButtonHoverBg;
  }
}
</style>
